import { RestClient } from "./restClient";

/** Which boundary a policy runs on. `both` runs on the prompt and the reply. */
export type PolicyPhase = "pre" | "post" | "both";

/** How a policy decides.
 *
 *  A `rule` is deterministic pattern matching and can PREVENT output. A
 *  `judge` asks an LLM and only advises — on a reply it runs after the text
 *  has already streamed, so it flags rather than prevents. The distinction is
 *  load-bearing for authors and is spelled out in the panel, not just here.
 */
export type PolicyKind = "rule" | "judge";

export interface RuleDefinition {
  patterns: string[];
  action: "block" | "redact";
}

export interface JudgeDefinition {
  instruction: string;
  action: "block" | "flag";
}

export type PolicyDefinition = RuleDefinition | JudgeDefinition;

/** One stored policy row, as every list/effective response shapes it. */
export interface PolicyRow {
  scope_tier: string;
  scope_id: string;
  key: string;
  phase: PolicyPhase;
  kind: PolicyKind;
  definition: PolicyDefinition;
  enabled: boolean;
  created_at: string | null;
  updated_at: string;
  updated_by: string | null;
}

/** A row in the effective (union) view.
 *
 *  `inherited` is true when the row's tier is BROADER than the queried scope —
 *  a platform or industry rule this company is subject to and cannot remove.
 *
 *  Two rows sharing a `key` both appear and both apply. That is the union
 *  working, not a collision: the panel must never render it as a conflict.
 */
export interface EffectiveRow extends PolicyRow {
  /** The tier that produced this row. DERIVED from the server's `scope_tier`
   *  in `listEffective` — the wire has no `tier` field, and reading one
   *  rendered "undefined requires redact" in the union sentence. */
  tier: string;
  /** True when a broader tier set this, so this scope can see it but not
   *  change it. DERIVED — the server does not send it. */
  inherited: boolean;
}

/** An upsert. `definition: null` DELETES the row so the scope re-inherits;
 *  omitting a field leaves it alone. Those are different requests, which is
 *  why this is not `Partial<PolicyRow>`. */
export interface PolicyUpdate {
  key: string;
  phase?: PolicyPhase;
  kind?: PolicyKind;
  definition?: PolicyDefinition | null;
  enabled?: boolean;
}

interface ListResponse {
  items: PolicyRow[];
}

interface EffectiveResponse {
  items: EffectiveRow[];
}

/**
 * The COMPANY-scoped slice of /v1/admin/policies, for the workspace
 * Organization settings panel.
 *
 * Deliberately narrow: a company admin can read the union they are subject to
 * and write only their own tier. Tier violations come back as 403 from the
 * server, which this client surfaces (RestError) rather than swallowing — a
 * silently empty panel is indistinguishable from a broken one.
 *
 * Not cached. Unlike the LLM setting, the effective view is a union across
 * tiers a platform admin can change at any time, so a stale read would tell a
 * company admin they are subject to rules that no longer exist.
 */
export class CompanyPoliciesApi {
  constructor(private client: RestClient) {}

  /** The company's OWN rows — the editable ones. */
  async listOwn(): Promise<PolicyRow[]> {
    const resp = await this.client.get<ListResponse>(
      "/v1/admin/policies/company",
    );
    return resp.items ?? [];
  }

  /** Everything in force for this company: its own rows plus the broader
   *  tiers it inherits. Rows sharing a key ALL apply. */
  async listEffective(companyId: number | string): Promise<EffectiveRow[]> {
    const resp = await this.client.get<EffectiveResponse>(
      `/v1/admin/policies/effective?company_id=${encodeURIComponent(String(companyId))}`,
    );
    // The wire carries `scope_tier` and no `inherited` flag. Deriving both here
    // keeps the shape mismatch in the one place that owns the wire format —
    // reading `row.tier` in the view rendered "undefined requires redact;
    // undefined additionally blocks", which is the union sentence teaching the
    // opposite of what it exists to teach.
    return (resp.items ?? []).map((row) => ({
      ...row,
      tier: row.scope_tier,
      inherited: row.scope_tier !== "company",
    }));
  }

  /** Create or update one company row. Returns the recomputed company list —
   *  the SERVER owns normalisation, so its answer replaces local state rather
   *  than the submitted patch. */
  async save(update: PolicyUpdate): Promise<PolicyRow[]> {
    // `{policies: {<key>: {...}}}` — the key addresses the row, it is not a
    // field of it. Sent flat, the server's `PolicyBody` ignores every extra
    // field and returns 200 OK having written nothing, so the panel reports a
    // saved policy over a table that never received it.
    const { key, ...fields } = update;
    const resp = await this.client.put<ListResponse>(
      "/v1/admin/policies/company",
      { policies: { [key]: fields } },
    );
    return resp.items ?? [];
  }

  /** Drop the company's row for a key so it re-inherits the broader tiers.
   *
   *  A null MAP VALUE is the delete verb. `phase`/`kind` are accepted for the
   *  call sites that already pass them, but the server addresses the row by
   *  `(tier, scope_id, key)` alone and ignores them on a delete. */
  async remove(
    key: string,
    _phase?: PolicyPhase,
    _kind?: PolicyKind,
  ): Promise<PolicyRow[]> {
    const resp = await this.client.put<ListResponse>(
      "/v1/admin/policies/company",
      { policies: { [key]: null } },
    );
    return resp.items ?? [];
  }
}
